import { useContext, useEffect, useRef, useState } from "react";
import { getSvcSettingsClient, type SettingsGroup } from "sj-ui-toolkit/src/client/svcSettings";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { AppContext } from "@/context/AppContext";
import {
  ALL_ASPEN_FEATURES_DISABLED,
  areAspenTopLevelFeaturesDisabled,
  ASPEN_FEATURE_META,
  DEFAULT_ASPEN_REENABLE_SNAPSHOT,
  type AspenFeatureKey,
  type AspenFeaturesState,
  type AspenTopLevelFeatureKey,
  getAspenSnapshotStorageKey,
  getAspenTopLevelSnapshot,
} from "@/constants/aspen";

const aspenFeaturesCache = new Map<string, AspenFeaturesState>();
const BACK_OFFICE_ASPEN_GROUP_NAME = "aspen.global";
const ASPEN_FEATURES_UPDATED_EVENT = "admin:aspen-features-updated";

interface AspenFeaturesUpdatedEventDetail {
  tenantName: string;
  features: AspenFeaturesState;
}

const readStoredSnapshot = (
  tenantName: string
): Record<AspenTopLevelFeatureKey, boolean> | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.sessionStorage.getItem(getAspenSnapshotStorageKey(tenantName));
    if (!raw) return null;

    const snapshot = JSON.parse(raw) as Partial<Record<"assist" | "guardian" | "adapt", boolean>>;
    if (
      typeof snapshot.assist !== "boolean" ||
      typeof snapshot.guardian !== "boolean" ||
      typeof snapshot.adapt !== "boolean"
    ) {
      return null;
    }

    return {
      assist: snapshot.assist,
      guardian: snapshot.guardian,
      adapt: snapshot.adapt,
    };
  } catch {
    return null;
  }
};

const writeStoredSnapshot = (
  tenantName: string,
  snapshot: ReturnType<typeof getAspenTopLevelSnapshot>
) => {
  if (typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(getAspenSnapshotStorageKey(tenantName), JSON.stringify(snapshot));
  } catch {
    // ignore sessionStorage write failures
  }
};

const buildFeatureState = (groups: SettingsGroup[]): AspenFeaturesState => {
  const assistGroup = groups.find(group => group.groupName === "aspen.assist");
  const guardianGroup = groups.find(group => group.groupName === "aspen.guardian");
  const adaptGroup = groups.find(group => group.groupName === "aspen.adapt");

  return {
    assist: (assistGroup?.values?.["hintsEnabled"] as boolean | undefined) ?? false,
    guardian: (guardianGroup?.values?.["enableAspenGuardian"] as boolean | undefined) ?? false,
    recordInstructionFiles:
      (guardianGroup?.values?.["recordInstructionFiles"] as boolean | undefined) ?? false,
    forwardCwes: (guardianGroup?.values?.["forwardCwes"] as boolean | undefined) ?? false,
    adapt: (adaptGroup?.values?.["enableAspenAdapt"] as boolean | undefined) ?? false,
    recordRepoName: (adaptGroup?.values?.["recordRepoName"] as boolean | undefined) ?? false,
    recordUsername: (adaptGroup?.values?.["recordUsername"] as boolean | undefined) ?? false,
    recordPrNumber: (adaptGroup?.values?.["recordPrNumber"] as boolean | undefined) ?? false,
  };
};

const resolveBackOfficeAspenEnabled = (groups: SettingsGroup[]): boolean => {
  const aspenGroup =
    groups.find(group => group.groupName === BACK_OFFICE_ASPEN_GROUP_NAME) ?? groups[0];
  const values = (aspenGroup?.values ?? {}) as Record<string, unknown>;
  return values["enableAspenGlobally"] !== false;
};

const emitAspenFeaturesUpdated = (tenantName: string, features: AspenFeaturesState): void => {
  if (!tenantName || typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent<AspenFeaturesUpdatedEventDetail>(ASPEN_FEATURES_UPDATED_EVENT, {
      detail: { tenantName, features },
    })
  );
};

export interface UseAspenFeaturesOptions {
  enabled?: boolean;
}

export const useAspenFeatures = ({ enabled = true }: UseAspenFeaturesOptions = {}) => {
  const {
    state: { viewOnly },
  } = useContext(AppContext);
  const currentTenantName = getCurrentSelectedTenant() ?? "";
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(true);
  const [isBackOfficeAspenEnabled, setIsBackOfficeAspenEnabled] = useState(true);
  const [features, setFeatures] = useState<AspenFeaturesState>({ ...ALL_ASPEN_FEATURES_DISABLED });
  const featuresRef = useRef(features);
  const preDisableSnapshot = useRef<ReturnType<typeof getAspenTopLevelSnapshot> | null>(null);

  useEffect(() => {
    featuresRef.current = features;
  }, [features]);

  useEffect(() => {
    if (!currentTenantName || typeof window === "undefined") return;

    const handleAspenFeaturesUpdated = (event: Event): void => {
      const customEvent = event as CustomEvent<AspenFeaturesUpdatedEventDetail>;
      const detail = customEvent.detail;

      if (!detail || detail.tenantName !== currentTenantName) {
        return;
      }

      setFeatures(detail.features);
      featuresRef.current = detail.features;
      aspenFeaturesCache.set(currentTenantName, detail.features);
    };

    window.addEventListener(ASPEN_FEATURES_UPDATED_EVENT, handleAspenFeaturesUpdated);

    return () => {
      window.removeEventListener(ASPEN_FEATURES_UPDATED_EVENT, handleAspenFeaturesUpdated);
    };
  }, [currentTenantName]);

  useEffect(() => {
    if (!enabled || !currentTenantName) {
      setFeatures({ ...ALL_ASPEN_FEATURES_DISABLED });
      featuresRef.current = { ...ALL_ASPEN_FEATURES_DISABLED };
      preDisableSnapshot.current = null;
      setIsBackOfficeAspenEnabled(true);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const load = async () => {
      try {
        const { promise: backOfficePromise } = getSvcSettingsClient().getBackOfficeSettings(
          currentTenantName,
          BACK_OFFICE_ASPEN_GROUP_NAME
        );
        const backOfficeData = await backOfficePromise;
        if (cancelled) return;

        const backOfficeEnabled = resolveBackOfficeAspenEnabled(backOfficeData?.groups ?? []);
        setIsBackOfficeAspenEnabled(backOfficeEnabled);

        if (!backOfficeEnabled) {
          const disabledFeatures = { ...ALL_ASPEN_FEATURES_DISABLED };
          setFeatures(disabledFeatures);
          featuresRef.current = disabledFeatures;
          preDisableSnapshot.current = null;
          aspenFeaturesCache.delete(currentTenantName);
          return;
        }

        const cachedFeatures = aspenFeaturesCache.get(currentTenantName);
        if (cachedFeatures) {
          setFeatures({ ...cachedFeatures });
          featuresRef.current = { ...cachedFeatures };
          if (!areAspenTopLevelFeaturesDisabled(cachedFeatures)) {
            writeStoredSnapshot(currentTenantName, getAspenTopLevelSnapshot(cachedFeatures));
          }
          return;
        }

        const { promise } = getSvcSettingsClient().getTenantSettings(currentTenantName, "aspen");
        const data = await promise;
        if (cancelled) return;

        const nextFeatures = buildFeatureState(data?.groups ?? []);
        setFeatures(nextFeatures);
        featuresRef.current = nextFeatures;
        aspenFeaturesCache.set(currentTenantName, nextFeatures);

        if (!areAspenTopLevelFeaturesDisabled(nextFeatures)) {
          writeStoredSnapshot(currentTenantName, getAspenTopLevelSnapshot(nextFeatures));
        }
      } catch (error) {
        console.error("Error fetching Aspen feature settings:", error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [currentTenantName, enabled]);

  const buildGroupPayload = (group: string, override: Record<string, boolean> = {}) => {
    const current = featuresRef.current;
    const payloads: Record<string, Record<string, boolean>> = {
      "aspen.assist": { hintsEnabled: current.assist },
      "aspen.guardian": {
        enableAspenGuardian: current.guardian,
        recordInstructionFiles: current.recordInstructionFiles,
        forwardCwes: current.forwardCwes,
      },
      "aspen.adapt": {
        enableAspenAdapt: current.adapt,
        recordRepoName: current.recordRepoName,
        recordUsername: current.recordUsername,
        recordPrNumber: current.recordPrNumber,
      },
    };

    return { ...payloads[group], ...override };
  };

  const handleSaveError = (detail: string) => {
    showToast({
      severity: "error",
      summary: "Unable to save Aspen settings",
      detail,
    });
  };

  const toggleFeature = async (key: AspenFeatureKey, value: boolean) => {
    if (!currentTenantName || !isBackOfficeAspenEnabled) return;

    const { group, field } = ASPEN_FEATURE_META[key];
    const previousFeatures = featuresRef.current;
    const enabledAdapt = key === "forwardCwes" && value;
    const nextFeatures = {
      ...previousFeatures,
      [key]: value,
      ...(enabledAdapt ? { adapt: true } : {}),
    };

    setFeatures(nextFeatures);
    featuresRef.current = nextFeatures;

    try {
      const { promise } = getSvcSettingsClient().updateTenantSettings(
        currentTenantName,
        group,
        buildGroupPayload(group, { [field]: value })
      );
      await promise;

      if (enabledAdapt) {
        const { promise: adaptPromise } = getSvcSettingsClient().updateTenantSettings(
          currentTenantName,
          "aspen.adapt",
          buildGroupPayload("aspen.adapt", { enableAspenAdapt: true })
        );
        await adaptPromise;
      }

      aspenFeaturesCache.set(currentTenantName, featuresRef.current);
      if (!areAspenTopLevelFeaturesDisabled(featuresRef.current)) {
        writeStoredSnapshot(currentTenantName, getAspenTopLevelSnapshot(featuresRef.current));
      }
      emitAspenFeaturesUpdated(currentTenantName, featuresRef.current);
    } catch (error) {
      console.error(`Error updating Aspen feature "${key}":`, error);
      setFeatures(previousFeatures);
      featuresRef.current = previousFeatures;
      handleSaveError(`"${key}" could not be updated. Please try again.`);
    }
  };

  const allDisabled = areAspenTopLevelFeaturesDisabled(features);

  const toggleAll = async (disabling: boolean) => {
    if (!currentTenantName || !isBackOfficeAspenEnabled) return;

    const currentFeatures = featuresRef.current;
    const snapshot = disabling
      ? DEFAULT_ASPEN_REENABLE_SNAPSHOT
      : (preDisableSnapshot.current ??
        readStoredSnapshot(currentTenantName) ??
        DEFAULT_ASPEN_REENABLE_SNAPSHOT);

    const assistValue = disabling ? false : Boolean(snapshot.assist);
    const guardianValue = disabling ? false : Boolean(snapshot.guardian);
    const adaptValue = disabling ? false : Boolean(snapshot.adapt);

    if (disabling) {
      const nextSnapshot = getAspenTopLevelSnapshot(currentFeatures);
      preDisableSnapshot.current = nextSnapshot;
      writeStoredSnapshot(currentTenantName, nextSnapshot);
    }

    try {
      await Promise.all([
        getSvcSettingsClient().updateTenantSettings(
          currentTenantName,
          "aspen.assist",
          buildGroupPayload("aspen.assist", { hintsEnabled: assistValue })
        ).promise,
        getSvcSettingsClient().updateTenantSettings(
          currentTenantName,
          "aspen.guardian",
          buildGroupPayload("aspen.guardian", { enableAspenGuardian: guardianValue })
        ).promise,
        getSvcSettingsClient().updateTenantSettings(
          currentTenantName,
          "aspen.adapt",
          buildGroupPayload("aspen.adapt", { enableAspenAdapt: adaptValue })
        ).promise,
      ]);
    } catch (error) {
      console.error("Error updating all Aspen features:", error);
      handleSaveError("Aspen feature settings could not be updated. Please try again.");
      return;
    }

    const nextFeatures: AspenFeaturesState = {
      ...currentFeatures,
      assist: assistValue,
      guardian: guardianValue,
      adapt: adaptValue,
    };

    if (!disabling) preDisableSnapshot.current = null;
    setFeatures(nextFeatures);
    featuresRef.current = nextFeatures;
    aspenFeaturesCache.set(currentTenantName, nextFeatures);

    if (!areAspenTopLevelFeaturesDisabled(nextFeatures)) {
      writeStoredSnapshot(currentTenantName, getAspenTopLevelSnapshot(nextFeatures));
    }
    emitAspenFeaturesUpdated(currentTenantName, nextFeatures);
  };

  return {
    features,
    isLoading,
    isBackOfficeAspenEnabled,
    viewOnly,
    currentTenantName,
    allDisabled,
    toggleFeature,
    toggleAll,
  };
};
